import { AdAnalyticsReport } from '@kaizenplatform/hugot-entity';
import { History } from 'history';
import { List } from 'immutable';
import { parse } from 'qs';
import Store from 'repatch';

import State from '../core/State';
import { OrganizationDetailFragment } from '../generated/graphql';
import ErrorPresenter from '../service/ErrorPresenter';
import MessageDispatcher from '../service/MessageDispatcher';
import AdAnalyticsReportWebApi from '../service/persistent/AdAnalyticsReportWebApi';
import OrganizationWebApi from '../service/persistent/OrganizationWebApi';
import {
  disposeAdAnalyticsReports,
  finishFetchingAdAnalyticsReports,
  setAdAnalyticsReports,
  startFetchingAdAnalyticsReports,
} from '../thunk/adAnalyticsReport';
import { setLastAccessedOrganization } from '../thunk/lastAccessedOrganization';

class ListAdAnalyticsReportUsecase {
  private readonly store: Store<State>;
  private readonly errorPresenter: ErrorPresenter;
  private readonly history: History;
  private readonly organizationWebApi: OrganizationWebApi;
  private readonly adAnalyticsReportWebApi: AdAnalyticsReportWebApi;
  private readonly messageDispatcher: MessageDispatcher;

  constructor({
    store,
    history,
    errorPresenter,
    messageDispatcher,
    organizationWebApi,
    adAnalyticsReportWebApi,
  }: {
    store: Store<State>;
    history: History;
    errorPresenter: ErrorPresenter;
    messageDispatcher: MessageDispatcher;
    organizationWebApi: OrganizationWebApi;
    adAnalyticsReportWebApi: AdAnalyticsReportWebApi;
  }) {
    this.store = store;
    this.history = history;
    this.errorPresenter = errorPresenter;
    this.messageDispatcher = messageDispatcher;
    this.organizationWebApi = organizationWebApi;
    this.adAnalyticsReportWebApi = adAnalyticsReportWebApi;
  }

  public prepare = async (): Promise<void> => {
    this.store.dispatch(startFetchingAdAnalyticsReports());
    // const { creativeID } = this.getQueryParameters();
    let reports: List<AdAnalyticsReport>;

    const query = parse(this.store.getState().location.search.slice(1));
    const organizationId = query.org || null;
    try {
      if (organizationId) {
        const organization = await this.organizationWebApi.getById(organizationId);
        this.store.dispatch(setLastAccessedOrganization(organization));
      }
    } catch (err) {
      this.errorPresenter.showAsFatal(err);
      return;
    }

    try {
      reports = await this.fetchAdAnalyticsReports();
    } catch (err) {
      this.store.dispatch(finishFetchingAdAnalyticsReports());
      this.store.dispatch(disposeAdAnalyticsReports());
      this.errorPresenter.showAsFatal(err);
      return;
    }

    this.store.dispatch(setAdAnalyticsReports(reports));
    this.store.dispatch(finishFetchingAdAnalyticsReports());
  };

  public fetchAdAnalyticsReports = async (): Promise<List<AdAnalyticsReport>> => {
    const state: State = this.store.getState();
    let organization: OrganizationDetailFragment | null = null;

    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    this.store.dispatch(setLastAccessedOrganization(organization));

    return await this.adAnalyticsReportWebApi.getAdAnalyticsReportsList({
      organizationId: organization ? organization.id : '',
    });
  };

  public tryDeleteAdAnalyticsReports = async ({ id }: { id: string }) => {
    try {
      await this.adAnalyticsReportWebApi.deleteReport({ id: id });
      this.messageDispatcher.notifyUnimportant('message.report_delete');
    } catch (err) {
      this.errorPresenter.showAsFatal(err);
    }
    this.prepare();
  };

  public goAnalyticsListPage = () => {
    const state: State = this.store.getState();
    let organization: OrganizationDetailFragment | null = null;
    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    const orgId = organization ? organization.id : '';

    this.history.push(`/ad-creatives${orgId !== '' ? `?org=${orgId}` : ''}`);
  };

  public goReportDetailPage = (id: string) => {
    const state: State = this.store.getState();
    let organization: OrganizationDetailFragment | null = null;
    if (state.lastAccessedOrganization) {
      organization = state.lastAccessedOrganization;
    } else {
      organization = state.availableOrganizations.items[0];
    }
    const orgId = organization ? organization.id : '';
    this.history.push(`/analytics-report/${orgId !== '' ? `?org=${orgId}&` : '?'}id=${id}`);
  };

  public deleteReport = (id: string) => {
    this.tryDeleteAdAnalyticsReports({ id });
  };

  public getQueryParameters = () => {
    const query = parse(this.store.getState().location.search.slice(1));
    const organizationID = query.org;
    const creativeID: string[] = query.creative ? query.creative.split(',') : [];

    return { organizationID, creativeID };
  };

  public dispose = (): void => {
    this.store.dispatch(disposeAdAnalyticsReports());
  };
}

export default ListAdAnalyticsReportUsecase;
